import React, { FunctionComponent, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik, FormikProps, FormikHelpers as FormikActions } from 'formik';
import { Grid, Typography, Button, Loader, Switch } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { IoIosAddCircleOutline } from 'react-icons/io';

// Components
import StyledContainer from 'components/StyledContainer';
import AssetPriceOptions from 'pages/MerchantPages/Assets/Price/containers/PriceOptions';
import PricesTable from 'pages/MerchantPages/Assets/Price/components/PricesTable';
import AccordionFooter from 'components/AccordionFooter';
import BaseModal, { ModalActions } from 'components/Modals/BaseModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import { FullHeightForm, ContentContainer, CenterContent, BlueTypography } from 'components/SharedStyledElements';
import FormAccordionHelper, {
  SectionType,
  PackagesSectionType,
} from 'pages/MerchantPages/Assets/components/AccordionHelper';
import RestrictedByRule from 'restrictions/roles/components/RestrictedByRule';
import ThemedMessage, { MessageVariants } from 'components/ThemedMessage';

// hooks
import { useBoolean } from 'hooks/useBoolean';
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// actions
import {
  ItemAccessTypes,
  ItemType,
  ItemAccessFee,
  setShouldCloseSection,
  createTemplatePrice,
  updateTemplatePrice,
  deleteTemplatePrice,
  removeAccessFee,
  saveSelectedFeeId,
  createItemAccessFee,
  updateItemAccessFee,
  saveSelectedTemplateFee,
  changeItemPlan,
} from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// types
import InAppIntegrationsState from 'ducks/merchantDucks/inAppIntegrations/types';
import { AccessFee } from 'ducks/merchantDucks/items/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// constants
import { paymentTypesList } from 'pages/MerchantPages/Assets/Price/components/forms/GeneralPriceInfo';
import {
  IOS_PAYMENT_PROVIDER_ID,
  AMAZON_PAYMENT_PROVIDER_ID,
  ROKU_PAYMENT_PROVIDER_ID,
  GOOGLE_PAYMENT_PROVIDER_ID,
} from 'constants/index';
import { seasonInvalidDatesMessage } from 'pages/MerchantPages/Assets/Price/SeasonSubscription/seasonSubscriptionConfig';
import Rules from 'restrictions/roles/constants/rules';

// utils
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import { validationSchema } from 'pages/MerchantPages/Assets/Price/validationSchema';

const { Container, Cell } = Grid;

export const CellStyled = styled(Cell)`
  padding: 2rem 6%;
`;

const StyledTypography = styled(Typography)`
  margin: 15px 0;
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

interface OwnProps {
  countrySets: Array<any>;
  accessTypes: ItemAccessTypes;
  accessFees: Array<AccessFee>;
  inAppIntegrations: InAppIntegrationsState;
  selectedFee?: any;
  accessControlTypeId: number;
  selectedFeeId: number;
  closePanel: () => void;
  isPackageSection?: boolean;
  itemId: number;
  itemType: string;
  isAccessFeesFetching?: boolean;
  isEditTemplate?: boolean;
  selectedTemplateFee?: any;
  selectedTemplateFeeId?: number;
  accordionLabel: string;
  isPlanSwitchEnabled: boolean;
  totalSubscriptions: number;
}

type Props = OwnProps;

interface FormValues {
  accessFeeDescription: string;
  paymentType: string;
  accessTypeId: number | string;
  amount: number;
  currency: string;
  expiresAt: Moment;
  countrySet: string;
  country: string;
  freeTrialDays: number;
  additionalFeeAmount: number;
  additionalFeeDescription?: string;
  startsAt: Moment;
  accessControlTypeId: number;
  isFreeTrialEnabled: boolean;
  isAdditionalFeeEnabled: boolean;
  isGeoRestrictionEnabled: boolean;
  isScheduleStartEnabled: boolean;
  isEditing: boolean;
  restrictionType: string;
  geoRestrictionType: number;
  feeId: number;
  itemId: number;
  isExternalInAppEnabled: boolean;
  googleInAppKeys: string;
  appleInAppKeys: string;
  amazonInAppKeys: string;
  rokuInAppKeys: string;
  seasonAnchorDate: Moment;
  seasonPriceAmount: number;
  offSeasonAccess: boolean;
}

export enum PaymentTypes {
  Ppv = '1',
  Subscription = '2',
  Season = '3',
}

export enum AccessTypes {
  CustomPpv = '21',
}

const inAppIdsMap = {
  googleInAppKeys: GOOGLE_PAYMENT_PROVIDER_ID,
  amazonInAppKeys: AMAZON_PAYMENT_PROVIDER_ID,
  appleInAppKeys: IOS_PAYMENT_PROVIDER_ID,
  rokuInAppKeys: ROKU_PAYMENT_PROVIDER_ID,
};

const AssetPriceContainer: FunctionComponent<Props> = ({
  accessTypes,
  countrySets,
  accessFees,
  inAppIntegrations,
  selectedFee,
  accessControlTypeId,
  selectedFeeId,
  closePanel,
  isPackageSection,
  itemId,
  itemType,
  isAccessFeesFetching,
  isEditTemplate = false,
  selectedTemplateFeeId = 0,
  selectedTemplateFee,
  accordionLabel,
  isPlanSwitchEnabled,
  totalSubscriptions,
}) => {
  const dispatch = useDispatch();

  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const isAddPriceSelected = useBoolean(false);
  const [isFeeSelected, setIsFeeSelected] = useState(false);
  const [assetRemovalId, setAssetRemovalId] = useState(0);
  const [feeId, setFeeId] = useState(0);
  const [hasActionError, setHasActionError] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues>({} as FormValues);

  const selectedItemFeeId = isEditTemplate ? selectedTemplateFeeId : selectedFeeId;
  const selectedItemFee = isEditTemplate ? selectedTemplateFee : selectedFee;
  const {
    amount,
    currency,
    description: accessFeeDescription,
    setup_fee: setupFee,
    trial_period: trialPeriod,
    access_type: { id: accessTypeId, name: accessTypeName },
    geo_restriction: geoRestriction,
    starts_at: startsAt,
    expires_at: expiresAt,
    created_at: createdAt,
    external_fees,
    seasonal_fee: seasonalFee,
  } = selectedItemFee || {
    description: '',
    amount: '',
    currency: 'EUR',
    setup_fee: {
      fee_amount: '',
      description: '',
    },
    trial_period: {
      quantity: '',
      description: '',
    },
    geo_restriction: {
      country_iso: null,
      type: null,
    },
    access_type: { id: 5, name: 'ppv' },
    starts_at: null,
    expires_at: null,
    created_at: null,
    external_fees: null,
    seasonal_fee: null,
  };
  const { fee_amount: additionalFeeAmount, description: additionalFeeDescription } = setupFee || {
    fee_amount: '',
    description: '',
  };
  const { quantity: freeTrialDays } = trialPeriod || {
    quantity: '',
  };
  const { country_iso: countryIso, type: restrictionType, country_set_id: countrySetId } = geoRestriction || {
    country_iso: null,
    type: null,
    country_set_id: null,
  };

  const {
    anchor_date: seasonAnchorDate,
    current_price_amount: seasonPriceAmount,
    off_season_access: offSeasonAccess,
  } = seasonalFee || {
    anchor_date: null,
    current_price_amount: '',
    off_season_access: false,
  };

  let accessType = accessTypeName;
  if (accessTypeName === 'ppv_custom') accessType = 'ppv';
  const geoRestrictionType = countrySetId ? 1 : 0;
  const paymentType: any = paymentTypesList.filter((type) => type.displayName === accessType)[0].value || 1;
  const isFreeTrialEnabled = freeTrialDays !== '';
  const isAdditionalFeeEnabled = additionalFeeAmount !== '';
  const isGeoRestrictionEnabled = restrictionType !== null;
  const isScheduleStartEnabled = startsAt !== null && startsAt !== createdAt;
  const isExternalInAppEnabled = Boolean(external_fees);

  useEffect(() => {
    setInitialValues({
      country: countryIso,
      countrySet: countrySetId,
      // Convert to string to match with changed amount value type
      amount: amount.toString(),
      currency,
      accessFeeDescription,
      additionalFeeAmount,
      additionalFeeDescription,
      freeTrialDays,
      accessTypeId: String(accessTypeId),
      paymentType,
      accessControlTypeId,
      isFreeTrialEnabled,
      isAdditionalFeeEnabled,
      isGeoRestrictionEnabled,
      isScheduleStartEnabled,
      isEditing: false,
      restrictionType,
      startsAt: startsAt ? moment.unix(startsAt) : moment(),
      expiresAt: expiresAt ? moment.unix(expiresAt) : moment(),
      geoRestrictionType: countrySetId ? 1 : 0,
      feeId: selectedItemFeeId,
      itemId,
      isExternalInAppEnabled,
      amazonInAppKeys: external_fees?.find((element: any) => element.payment_provider_id === AMAZON_PAYMENT_PROVIDER_ID)
        ?.external_id,
      appleInAppKeys: external_fees?.find((element: any) => element.payment_provider_id === IOS_PAYMENT_PROVIDER_ID)
        ?.external_id,
      googleInAppKeys: external_fees?.find((element: any) => element.payment_provider_id === GOOGLE_PAYMENT_PROVIDER_ID)
        ?.external_id,
      rokuInAppKeys: external_fees?.find((element: any) => element.payment_provider_id === ROKU_PAYMENT_PROVIDER_ID)
        ?.external_id,
      seasonAnchorDate: seasonAnchorDate ? moment.unix(seasonAnchorDate) : moment(),
      seasonPriceAmount,
      offSeasonAccess,
    });
    totalSubscriptions <= 1 && isPlanSwitchEnabled && dispatch(changeItemPlan(itemId));
  }, [
    countryIso,
    countrySetId,
    amount,
    currency,
    accessFeeDescription,
    additionalFeeAmount,
    additionalFeeDescription,
    freeTrialDays,
    accessTypeId,
    paymentType,
    accessControlTypeId,
    isFreeTrialEnabled,
    isAdditionalFeeEnabled,
    isGeoRestrictionEnabled,
    isScheduleStartEnabled,
    restrictionType,
    startsAt,
    expiresAt,
    countrySetId,
    selectedItemFeeId,
    itemId,
    isExternalInAppEnabled,
    external_fees,
    seasonAnchorDate,
    seasonPriceAmount,
    offSeasonAccess,
    totalSubscriptions,
  ]);

  const deleteAssetPrice = () => {
    return isEditTemplate
      ? dispatch(deleteTemplatePrice(assetRemovalId, feeId))
      : dispatch(removeAccessFee(assetRemovalId, feeId));
  };

  const handleShowModal = (
    assetId: number,
    priceId: number,
    startsAtDate: number,
    expiresAtDate: number,
    isSeasonFee: boolean
  ) => {
    if (moment().isBetween(moment.unix(startsAtDate), moment.unix(expiresAtDate)) && isSeasonFee) {
      dispatch(openModal(ModalNames.DeleteSeasonAccessPrice));
    } else {
      dispatch(openModal(ModalNames.DeleteAssetPrice));
      setAssetRemovalId(assetId);
      setFeeId(priceId);
    }
  };

  const selectAccessFee = (id: number) => {
    if (isEditTemplate) {
      dispatch(saveSelectedTemplateFee(id));
    } else {
      dispatch(saveSelectedFeeId(id));
    }
    setIsFeeSelected(true);
  };

  const handleBackToAccessFees = (resetForm?: () => void) => {
    // Deselect any access fee previously edited
    dispatch(saveSelectedFeeId(0));
    setIsFeeSelected(false);
    if (isEditTemplate) {
      dispatch(saveSelectedTemplateFee(0));
    }
    dispatch(setShouldCloseSection(true));
    if (resetForm) {
      resetForm();
    }
  };

  const handleAddNewPrice = () => {
    setIsFeeSelected(true);
  };

  const handleCancel = (resetForm: () => void) => () => {
    dispatch(saveSelectedFeeId(0));
    if (isEditTemplate) {
      dispatch(saveSelectedTemplateFee(0));
    }
    resetForm();
    closePanel();
  };

  useEffect(() => {
    if (isFeeSelected) {
      isAddPriceSelected.setTrue();
    } else {
      isAddPriceSelected.setFalse();
    }
  }, [isFeeSelected]);

  const renderGoToPackagesLink = (type: string) => {
    if (!isPackageSection && type === ItemType.Package) {
      return (
        <Cell width={12}>
          <NavLink to="/assets/packages">Go to packages &rarr;</NavLink>
        </Cell>
      );
    }
    return null;
  };

  const renderPriceTable = (type: string, typeAccessFees: Array<AccessFee>) => {
    return (
      <Cell width={12}>
        <Container columns={12} rowGap="1rem">
          <Cell width={12}>
            {!isEditTemplate && <Typography variant="p">{capitalize(type)} prices</Typography>}
            {typeAccessFees.length !== 0 ? (
              <PricesTable
                type={type}
                prices={typeAccessFees}
                selectAccessFee={selectAccessFee}
                handleShowModal={handleShowModal}
                isPackageSection={isPackageSection}
              />
            ) : (
              <Typography variant="p">There are no available {type} prices.</Typography>
            )}
          </Cell>
          <RestrictedByRule rule={Rules.READONLY_ASSETS_AND_PACKAGES}>{renderGoToPackagesLink(type)}</RestrictedByRule>
        </Container>
      </Cell>
    );
  };

  const renderSubscriptionSwitch = () => {
    return (
      <Cell width={12}>
        <Container columns={12} rowGap="1rem">
          <Cell width={12}>
            <Switch
              tag="toggle_subscription_plan"
              checked={isPlanSwitchEnabled}
              id="subscriptionPlanChange"
              label="Subscription plan change"
              onChange={() => dispatch(changeItemPlan(itemId, !isPlanSwitchEnabled))}
            />
            <StyledTypography variant="p">
              When enabled, the viewer will be able to change their subscription plan.
            </StyledTypography>
            {isPlanSwitchEnabled && (
              <ThemedMessage variant={MessageVariants.INFO}>
                Please note that if you disable it after some viewers already downgraded the subscription, their plan
                will be changed when the current one expires.
              </ThemedMessage>
            )}
          </Cell>
        </Container>
      </Cell>
    );
  };

  const renderPriceTables = (assetAccessFees: any[], packageAccessFees: any[]) => {
    if (isAccessFeesFetching) return <Loader />;
    return (
      <>
        {!isPackageSection && renderPriceTable('asset', assetAccessFees)}
        {totalSubscriptions > 1 && !isPackageSection && renderSubscriptionSwitch()}
        {!isEditTemplate && renderPriceTable('package', packageAccessFees)}
        {totalSubscriptions > 1 && isPackageSection && renderSubscriptionSwitch()}
      </>
    );
  };

  const renderAccessFees = () => {
    const assetAccessFees: any[] = [];
    const packageAccessFees: any[] = [];

    accessFees.forEach((fee) => {
      if (fee?.item?.item_type?.content_type === ItemType.Package) {
        packageAccessFees.push(fee);
      } else {
        assetAccessFees.push(fee);
      }
    });

    return (
      <>
        <CellStyled>
          <Container columns={12} rowGap="1.5rem">
            <Cell width={12}>
              <RestrictedByRule rule={Rules.READONLY_ASSETS_AND_PACKAGES}>
                <Button
                  tag="button_new_price"
                  type="button"
                  icon={<IoIosAddCircleOutline />}
                  onClick={handleAddNewPrice}
                >
                  New price
                </Button>
              </RestrictedByRule>
            </Cell>
            {renderPriceTables(assetAccessFees, packageAccessFees)}
          </Container>
        </CellStyled>
        <ConfirmModal
          modalName={ModalNames.DeleteAssetPrice}
          handleConfirm={deleteAssetPrice}
          message="Are you sure you want to delete the prices?"
        />
        <BaseModal
          modalName={ModalNames.DeleteSeasonAccessPrice}
          title="Delete season access fee"
          render={({ closeModal }: ModalActions) => (
            <>
              <BlueTypography variant="h5">
                You are not allowed to delete a season access fee during the season.
              </BlueTypography>
              <StyledContainer columns={3} alignContent="center" justifyContent="center" margin="2rem 0 0 0">
                <Cell middle left={3}>
                  <Button fullWidth onClick={closeModal} buttonModifiers={['buttonPrimary']}>
                    Close
                  </Button>
                </Cell>
              </StyledContainer>
            </>
          )}
        />
      </>
    );
  };

  const isEditAccessFeeScreen = !!selectedItemFee;

  const editSeasonFeeDisabled =
    accessType === 'season' &&
    isEditAccessFeeScreen &&
    moment().isBetween(moment.unix(startsAt), moment.unix(expiresAt));

  const onSubmitPriceForm = async (data: FormValues, { setSubmitting }: FormikActions<FormValues>) => {
    setHasActionError(false);
    const {
      accessTypeId: dataAccessTypeID,
      accessFeeDescription: dataDescription,
      amount: dataAmount,
      currency: dataCurrency,
      isFreeTrialEnabled: dataIsFreeTrialEnabled,
      paymentType: dataPaymentType,
      isAdditionalFeeEnabled: dataIsAdditionalFeeEnabled,
      additionalFeeAmount: dataAdditionalFeeAmount,
      additionalFeeDescription: dataAdditionalFeeDescription,
      isGeoRestrictionEnabled: dataIsGeoRestrictionEnabled,
      geoRestrictionType: dataGeoRestrictionType,
      countrySet: dataCountrySet,
      country: dataCountry,
      isScheduleStartEnabled: dataIsScheduleStartEnabled,
      startsAt: dataStartsAt,
      restrictionType: dataRestrictionType,
      expiresAt: dataExpiresAt,
      freeTrialDays: dataFreeTrialDays,
      isExternalInAppEnabled: dataIsExternalAppEnabled,
      seasonAnchorDate: dataSeasonAnchorDate,
      seasonPriceAmount: dataSeasonPriceAmount,
      offSeasonAccess: dataOffSeasonAccess,
    } = data;

    const priceObjectForUpdate: ItemAccessFee = {
      itemId,
      accessTypeId: dataAccessTypeID,
      accessFeeDescription: dataDescription,
      amount: dataAmount,
      currency: dataCurrency,
      itemType,
      isSeasonAccessFee: dataPaymentType === PaymentTypes.Season,
    };

    if (
      dataIsFreeTrialEnabled &&
      (dataPaymentType === PaymentTypes.Subscription || dataPaymentType === PaymentTypes.Season)
    ) {
      priceObjectForUpdate.freeTrialDays = dataFreeTrialDays;
    }
    if (
      dataIsAdditionalFeeEnabled &&
      (dataPaymentType === PaymentTypes.Subscription || dataPaymentType === PaymentTypes.Season)
    ) {
      priceObjectForUpdate.additionalFeeAmount = dataAdditionalFeeAmount;
      priceObjectForUpdate.additionalFeeDescription = dataAdditionalFeeDescription;
    }

    if (dataIsGeoRestrictionEnabled) {
      if (dataGeoRestrictionType === 0) {
        priceObjectForUpdate.country = dataCountry;
        priceObjectForUpdate.restrictionType = dataRestrictionType;
      } else {
        priceObjectForUpdate.countrySet = dataCountrySet;
      }
    }
    if (
      (dataIsScheduleStartEnabled && dataPaymentType === PaymentTypes.Ppv) ||
      dataPaymentType === PaymentTypes.Season
    ) {
      priceObjectForUpdate.startsAt = dataStartsAt;
    }

    if (dataPaymentType === PaymentTypes.Season) {
      priceObjectForUpdate.seasonAnchorDate = dataSeasonAnchorDate;
      priceObjectForUpdate.seasonPriceAmount = dataSeasonPriceAmount;
      priceObjectForUpdate.offSeasonAccess = dataOffSeasonAccess;
    }
    if (dataIsExternalAppEnabled) {
      // transform the external fees to array of objects { payment_provider_id: number; external_id: string; } and discard the null values
      priceObjectForUpdate.externalFees = ['googleInAppKeys', 'appleInAppKeys', 'amazonInAppKeys', 'rokuInAppKeys']
        .map((item) => ({
          payment_provider_id: inAppIdsMap[item],
          external_id: data[item]?.trim(),
        }))
        .filter((item) => item.external_id);
    }

    // When user has selected custom ppv option
    if (dataAccessTypeID === AccessTypes.CustomPpv || dataPaymentType === PaymentTypes.Season) {
      priceObjectForUpdate.expiresAt = dataExpiresAt;
    }

    let hasError: any;

    if (selectedItemFeeId) {
      if (isEditTemplate) {
        hasError = await dispatch(updateTemplatePrice(selectedItemFeeId, priceObjectForUpdate));
      } else {
        hasError = await dispatch(updateItemAccessFee(selectedItemFeeId, priceObjectForUpdate));
      }
    } else if (isEditTemplate) {
      hasError = await dispatch(createTemplatePrice(priceObjectForUpdate));
    } else {
      hasError = await dispatch(createItemAccessFee(priceObjectForUpdate));
    }

    if (!hasError) {
      if (dataPaymentType !== PaymentTypes.Season || isEditAccessFeeScreen) {
        handleBackToAccessFees();
      }
    } else {
      setHasActionError(true);
    }
    setSubmitting(false);
  };

  return isAddPriceSelected.value ? (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onSubmitPriceForm}
    >
      {({ resetForm, isValid, isSubmitting, errors, dirty, values }: FormikProps<FormValues>) => {
        const areInitialValuesUpdated = !isEqual(values, initialValues);
        // Asset access fees save button disable conditions
        const disablePriceSaveButton =
          isFeeSelected && (!areInitialValuesUpdated || !isValid || isSubmitting || !dirty);

        return (
          <FullHeightForm>
            <FormAccordionHelper
              initialValues={initialValues}
              closePanel={closePanel}
              index={isPackageSection ? PackagesSectionType.PriceOptions : SectionType.PriceOptions}
            />
            <ContentContainer>
              <CenterContent>
                <AssetPriceOptions
                  accessTypes={accessTypes}
                  countrySets={countrySets}
                  paymentType={paymentType}
                  accessTypeId={accessTypeId}
                  handleBackToAccessFees={() => handleBackToAccessFees(resetForm)}
                  inAppIntegrations={inAppIntegrations}
                  isEditAccessFeeScreen={isEditAccessFeeScreen}
                  geoRestrictionTypeTabIndex={geoRestrictionType}
                  hasActionError={hasActionError}
                  editSeasonFeeDisabled={editSeasonFeeDisabled}
                  isReadOnly={isReadOnly}
                  selectedFeeId={selectedItemFeeId}
                  isTemplate={isEditTemplate}
                />
                {isEditAccessFeeScreen && (
                  <ThemedMessage variant={MessageVariants.INFO}>
                    If you update the price amount after some viewers have already paid or updated their subscription
                    plan, future rebills will be based on the old price.
                  </ThemedMessage>
                )}
              </CenterContent>
              <AccordionFooter
                disabled={disablePriceSaveButton}
                handleCancel={handleCancel(resetForm)}
                buttonTooltipContent={errors.startsAt && seasonInvalidDatesMessage}
                accordionLabel={accordionLabel}
              />
            </ContentContainer>
          </FullHeightForm>
        );
      }}
    </Formik>
  ) : (
    renderAccessFees()
  );
};

export default AssetPriceContainer;
